import { useCallback, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { FxAlert, FxButton, FxIcon, FxPanel, FxSegmentedTabs, FxSpinner } from '@/components/fx'
import { adminApi, ApiError, isJobActive, templatesApi } from '@/lib/api'
import type { Job, JobList } from '@/types/domain'

import { ConfirmDialog } from './ConfirmDialog'
import {
  POLL_BUDGET_MS,
  POLL_INTERVAL_MS,
  filterJobs,
  jobCounts,
  jobStatusView,
  pollInterval,
  progressLabel,
  shortJobId,
  startedLabel,
  templateLabel,
  type JobFilterValue,
} from './jobs'

/**
 * How many jobs are fetched at once.
 *
 * The filter counts and the three buckets are computed over exactly these
 * rows, because `GET /v1/admin/jobs` filters by a single status and "Aktiv"
 * is two of them — asking the gateway per bucket would give three listings
 * whose totals do not add up. The panel says so when the gateway holds more
 * than this.
 */
const PAGE_SIZE = 100

/**
 * "Aufträge": the analysis jobs of every form, with the two mutations that
 * belong next to them.
 *
 * Ported from the retired admin build's `Jobs.tsx` / `useJobs.ts`: the
 * endpoint, the two mutations and the "poll only while something is active"
 * rule. Dropped: its `window.confirm`, its English column headings, its
 * `jobStats` query (a second source of numbers that could disagree with the
 * table it sat above) and the stat tiles the task explicitly leaves behind.
 */
export function JobsPanel() {
  const queryClient = useQueryClient()
  const [filter, setFilter] = useState<JobFilterValue>('alle')
  const [confirming, setConfirming] = useState<Job | null>(null)

  const [startedAt, setStartedAt] = useState(() => Date.now())
  const startedAtRef = useRef(startedAt)
  startedAtRef.current = startedAt
  // The cadence depends on the very rows this query holds, so the last answer
  // is read back through a ref: react-query calls `refetchInterval` after each
  // fetch, by which time the ref carries what this render saw.
  const activeRef = useRef(false)

  const jobsQuery = useQuery<JobList>({
    queryKey: ['verwaltung', 'jobs'],
    queryFn: () =>
      adminApi.getJobs({ limit: PAGE_SIZE, sort_by: 'created_at', sort_order: 'desc' }) as Promise<JobList>,
    retry: false,
    refetchInterval: () =>
      pollInterval({
        active: activeRef.current,
        elapsedMs: Date.now() - startedAtRef.current,
        intervalMs: POLL_INTERVAL_MS,
      }),
  })

  const jobs = useMemo(() => jobsQuery.data?.jobs ?? [], [jobsQuery.data])
  activeRef.current = jobs.some(isJobActive)

  // One listing for the whole table rather than a request per row. The
  // gateway caps it at 100 templates, so a job on a template beyond that
  // keeps its id — see `templateLabel`.
  const templatesQuery = useQuery({
    queryKey: ['verwaltung', 'template-names'],
    queryFn: () => templatesApi.list({ pageSize: 100 }),
    staleTime: 5 * 60_000,
    retry: false,
  })
  const names = useMemo(
    () => new Map((templatesQuery.data ?? []).map((t) => [t.id, t.title])),
    [templatesQuery.data],
  )

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['verwaltung', 'jobs'] }),
    [queryClient],
  )

  const cancel = useMutation({
    mutationFn: (jobId: string) => adminApi.cancelJob(jobId),
    // The worker only notices between documents, so the row's new state is the
    // gateway's to report — invalidate and read it back rather than guess.
    onSuccess: () => invalidate(),
  })
  const retry = useMutation({
    mutationFn: (jobId: string) => adminApi.retryJob(jobId),
    onSuccess: () => invalidate(),
  })

  const refresh = useCallback(() => {
    setStartedAt(Date.now())
    void jobsQuery.refetch()
  }, [jobsQuery])

  const counts = jobCounts(jobs)
  const rows = filterJobs(jobs, filter)
  const total = jobsQuery.data?.total ?? jobs.length
  const truncated = total > jobs.length
  const anyUnnamed = rows.some((job) => !names.has(job.template_id))
  const paused = Date.now() - startedAt >= POLL_BUDGET_MS && activeRef.current
  const mutationError = cancel.error ?? retry.error

  return (
    <FxPanel
      title="Aufträge"
      action={
        <FxSegmentedTabs
          aria-label="Aufträge filtern"
          className="gap-2"
          tabWidth={150}
          items={[
            { value: 'alle', label: `Alle (${counts.alle})` },
            { value: 'aktiv', label: `Aktiv (${counts.aktiv})` },
            { value: 'probleme', label: `Problemfälle (${counts.probleme})` },
          ]}
          active={filter}
          onSelect={(value) => setFilter(value as JobFilterValue)}
        />
      }
    >
      {mutationError && (
        <FxAlert tone="danger" title="Die Aktion ist fehlgeschlagen" className="mb-6">
          <p>{ApiError.from(mutationError).message}</p>
        </FxAlert>
      )}

      {jobsQuery.isLoading ? (
        <FxSpinner label="Aufträge werden geladen …" />
      ) : jobsQuery.isError ? (
        <div className="flex flex-col gap-4">
          <FxAlert tone="danger" title="Aufträge nicht abrufbar">
            <p>{ApiError.from(jobsQuery.error).message}</p>
          </FxAlert>
          <div>
            <FxButton variant="outlined" icon="refresh" onClick={refresh}>
              Erneut laden
            </FxButton>
          </div>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-body text-text-muted">
          {jobs.length === 0
            ? 'Zurzeit liegen keine Aufträge vor.'
            : 'In dieser Auswahl liegt zurzeit kein Auftrag.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-[calc(100%+48px)] -mx-6 border-separate text-body [border-spacing:24px_0]">
            <caption className="sr-only">Aufträge aller Musterformulare</caption>
            <thead>
              <tr>
                {['Auftrag', 'Musterformular', 'Status', 'Fortschritt', 'Gestartet', 'Aktion'].map(
                  (heading, index) => (
                    <th key={heading} scope="col" className="pb-[14px] text-left font-normal">
                      <span
                        className={`block whitespace-nowrap rounded-sm bg-fill px-2 py-[3px] ${
                          index === 5 ? 'sr-only' : ''
                        }`}
                      >
                        {heading}
                      </span>
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((job) => {
                const status = jobStatusView(job.status)
                const template = templateLabel(job.template_id, names)
                const busy = cancel.isPending || retry.isPending
                return (
                  <tr key={job.id}>
                    <td className="h-table-row whitespace-nowrap tabular-nums" title={job.id}>
                      {shortJobId(job.id)}
                    </td>
                    <td>
                      {template.named ? (
                        template.label
                      ) : (
                        <span className="tabular-nums text-text-muted" title={job.template_id}>
                          {shortJobId(template.label)}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`inline-flex items-center gap-1.5 ${status.className}`}>
                        <FxIcon name={status.icon} size={18} />
                        {status.word}
                      </span>
                      {/* The queue's own error text, on the one status where
                          it says something: a failed job's row is where the
                          reason is looked for, and this panel has no detail
                          view to send anyone to. Not shown for `cancelled`,
                          whose error reads "Cancelled". */}
                      {job.status === 'failed' && job.error ? (
                        <span className="mt-0.5 block max-w-[28ch] break-words text-caption text-text-muted">
                          {job.error}
                        </span>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap text-right tabular-nums">
                      {progressLabel(job)}
                    </td>
                    <td className="whitespace-nowrap tabular-nums">{startedLabel(job)}</td>
                    <td className="text-right">
                      {isJobActive(job) ? (
                        <FxButton
                          variant="tonal"
                          className="h-8 px-3"
                          disabled={busy}
                          onClick={() => setConfirming(job)}
                        >
                          Abbrechen
                        </FxButton>
                      ) : job.status === 'failed' ? (
                        <FxButton
                          variant="tonal"
                          className="h-8 px-3"
                          disabled={busy}
                          onClick={() => retry.mutate(job.id)}
                        >
                          Wiederholen
                        </FxButton>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-small text-text-muted">
        Aufträge aller Musterformulare. Ein abgebrochener Auftrag ist kein Fehler – der Worker hält
        zwischen zwei Dokumenten an, und die Dokumente, die er nicht mehr erreicht hat, stehen wieder
        auf „neu“.
      </p>
      {truncated && (
        <p className="mt-1 text-small text-text-muted">
          Angezeigt werden die {jobs.length} neuesten von {total} Aufträgen; die Zahlen in der
          Auswahl zählen nur diese.
        </p>
      )}
      {anyUnnamed && (
        <p className="mt-1 text-small text-text-muted">
          Zu einzelnen Aufträgen liefert die Liste nur die Kennung des Musterformulars – etwa weil es
          gelöscht wurde oder außerhalb der abgerufenen Formulare liegt.
        </p>
      )}
      {paused && (
        <p className="mt-1 text-small text-text-muted">
          Die automatische Aktualisierung ist pausiert.{' '}
          <button
            type="button"
            onClick={refresh}
            className="text-primary-text underline underline-offset-2"
          >
            Jetzt aktualisieren
          </button>
        </p>
      )}

      <ConfirmDialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title="Auftrag abbrechen?"
        description={
          <p>
            Der Worker hält zwischen zwei Dokumenten an. Was bereits ausgewertet ist, bleibt
            erhalten; die Dokumente, die er nicht mehr erreicht hat, stehen wieder auf „neu“ und
            können erneut ausgewertet werden.
          </p>
        }
        confirmLabel="Auftrag abbrechen"
        pending={cancel.isPending}
        onConfirm={() => {
          if (confirming) cancel.mutate(confirming.id)
          setConfirming(null)
        }}
      />
    </FxPanel>
  )
}
